import { categories } from "@sobidogam/catalog";
import {
	formatKstDate,
	formatWon,
	type PeriodKind,
	type ResolvedPeriod,
	resolvePeriod,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import { monthStartDayPref, readStoreRules } from "./prefs";
import { Sheet } from "./Sheet";
import { normalizeTagLabel, type TagDef } from "./tags";

const PERIOD_LABELS: Record<PeriodKind, string> = {
	this_month: "이번 달",
	last_month: "지난달",
	last_3_months: "최근 3개월",
	custom: "직접 지정",
};

export interface PeriodChoice {
	kind: PeriodKind | "all";
	custom: { from: string; to: string };
}

export function resolvePeriodChoice(
	choice: PeriodChoice,
): ResolvedPeriod | null {
	if (choice.kind === "all") return null;
	return resolvePeriod(
		choice.kind,
		new Date(),
		monthStartDayPref(),
		choice.custom,
	);
}

/** #26: period chips over the record list. */
export function PeriodFilter({
	choice,
	onChange,
}: {
	choice: PeriodChoice;
	onChange: (choice: PeriodChoice) => void;
}) {
	return (
		<div className="period-filter">
			<fieldset className="chip-row">
				<legend className="sr-only">기간</legend>
				{(
					[
						"all",
						"this_month",
						"last_month",
						"last_3_months",
						"custom",
					] as const
				).map((kind) => (
					<button
						key={kind}
						type="button"
						aria-pressed={choice.kind === kind}
						onClick={() => onChange({ ...choice, kind })}
					>
						{kind === "all" ? "전체" : PERIOD_LABELS[kind]}
					</button>
				))}
			</fieldset>
			{choice.kind === "custom" ? (
				<div className="custom-range">
					<label>
						시작일
						<input
							type="date"
							value={choice.custom.from}
							onChange={(event) =>
								onChange({
									...choice,
									custom: { ...choice.custom, from: event.target.value },
								})
							}
						/>
					</label>
					<label>
						끝일
						<input
							type="date"
							value={choice.custom.to}
							onChange={(event) =>
								onChange({
									...choice,
									custom: { ...choice.custom, to: event.target.value },
								})
							}
						/>
					</label>
				</div>
			) : null}
		</div>
	);
}

/** #28: one store's visits, total, average and the last five records. */
export function StoreSummary({
	storeKey,
	receipts,
	onClose,
	onRuleChange,
	onOpenRecord,
}: {
	storeKey: string;
	receipts: Receipt[];
	onClose: () => void;
	onRuleChange: (storeKey: string, categoryId: string | null) => void;
	onOpenRecord: (receiptId: string) => void;
}) {
	const records = receipts
		.filter((receipt) => !receipt.deletedAt && receipt.storeKey === storeKey)
		.sort((a, b) => b.transactedAt.localeCompare(a.transactedAt));
	const total = records.reduce((sum, receipt) => sum + receipt.totalAmount, 0);
	const first = records.at(-1);
	const [rule, setRule] = useState(() => readStoreRules()[storeKey] ?? "");
	const name = records[0]?.storeDisplay ?? storeKey;
	const sensitive = records.some((receipt) => receipt.sensitiveMasked);
	return (
		<Sheet title={name} onClose={onClose}>
			<dl className="store-summary">
				<div>
					<dt>방문</dt>
					<dd>{records.length}회</dd>
				</div>
				<div>
					<dt>합계</dt>
					<dd className="amount">{formatWon(total)}</dd>
				</div>
				<div>
					<dt>{records.length > 1 ? "평균" : "처음 간 날"}</dt>
					<dd className={records.length > 1 ? "amount" : ""}>
						{records.length > 1
							? formatWon(total / records.length)
							: first
								? formatKstDate(first.transactedAt)
								: "—"}
					</dd>
				</div>
			</dl>
			<h3 className="sheet-subhead">최근 기록</h3>
			<ul className="store-recent">
				{records.slice(0, 5).map((receipt) => (
					<li key={receipt.receiptId}>
						<button
							type="button"
							onClick={() => onOpenRecord(receipt.receiptId)}
						>
							<span>{formatKstDate(receipt.transactedAt)}</span>
							<b className="amount">{formatWon(receipt.totalAmount)}</b>
						</button>
					</li>
				))}
			</ul>
			{sensitive ? null : (
				<label className="store-rule">
					다음부터 이 매장의 분류
					<select
						value={rule}
						onChange={(event) => {
							setRule(event.target.value);
							onRuleChange(storeKey, event.target.value || null);
						}}
					>
						<option value="">자동으로 추정</option>
						{categories
							.filter((category) => !category.sensitive)
							.map((category) => (
								<option key={category.categoryId} value={category.categoryId}>
									{category.nameKo}
								</option>
							))}
					</select>
				</label>
			)}
		</Sheet>
	);
}

/** #30: create, rename and delete tags. Deleting only unlinks records. */
export function TagManager({
	tags,
	usage,
	onChange,
}: {
	tags: TagDef[];
	usage: Record<string, number>;
	onChange: (tags: TagDef[], removedId?: string) => void;
}) {
	const [draft, setDraft] = useState("");
	const [names, setNames] = useState<Record<string, string>>({});
	const add = () => {
		const label = normalizeTagLabel(draft);
		if (!label || tags.some((tag) => tag.label === label)) return;
		onChange([...tags, { id: `tag-${crypto.randomUUID()}`, label }]);
		setDraft("");
	};
	return (
		<div className="tag-manager">
			{tags.length === 0 ? (
				<p className="field-hint">
					여행·경조사처럼 분류를 가로지르는 묶음을 만들어요.
				</p>
			) : (
				<ul>
					{tags.map((tag) => (
						<li key={tag.id}>
							<input
								aria-label={`${tag.label} 이름`}
								value={names[tag.id] ?? tag.label}
								maxLength={20}
								onChange={(event) =>
									setNames({ ...names, [tag.id]: event.target.value })
								}
								onBlur={() => {
									const label = normalizeTagLabel(names[tag.id] ?? tag.label);
									if (!label || label === tag.label) return;
									onChange(
										tags.map((item) =>
											item.id === tag.id ? { ...item, label } : item,
										),
									);
								}}
							/>
							<small>{usage[tag.id] ?? 0}건</small>
							<button
								type="button"
								className="quiet-button"
								onClick={() =>
									onChange(
										tags.filter((item) => item.id !== tag.id),
										tag.id,
									)
								}
							>
								지우기
							</button>
						</li>
					))}
				</ul>
			)}
			<div className="tag-add">
				<input
					aria-label="새 태그 이름"
					placeholder="예: 제주 여행"
					value={draft}
					maxLength={20}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							add();
						}
					}}
				/>
				<button
					type="button"
					className="button button-small"
					disabled={!normalizeTagLabel(draft)}
					onClick={add}
				>
					태그 만들기
				</button>
			</div>
		</div>
	);
}

export function categoryName(categoryId: string): string {
	return (
		categories.find((category) => category.categoryId === categoryId)?.nameKo ??
		"기타"
	);
}
